import { AuthenticatedGuard } from "@/components/authenticated-guard";
import PageHeader from "@/components/page-header";
import { TitleRequestForm } from "@/components/title/title-request-form";
import { TitleRequestStatusBadge } from "@/components/title/title-request-status-badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { UserType } from "@/constants";
import { titleAuthConfigured } from "@/lib/title-auth";
import { getTitleLoginConnection } from "@/lib/title-login-connection";
import type { TitleRequestDetail } from "@/lib/title-request-api";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

function Confirmation({ request }: { request: TitleRequestDetail }) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <PageHeader
        title="Request submitted"
        subtitle="Your request has been added to your account."
        hideBackButton
      />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Status
            <TitleRequestStatusBadge status={request.status} />
          </CardTitle>
          <CardDescription>
            Hera will respond using {request.requester_email}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p>
            This request is on your account.{" "}
            <Link to="/title/requests" className="underline">
              View your requests
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function TitleRequestHome() {
  const { getAccessTokenSilently } = useAuth0();
  const [created, setCreated] = useState<TitleRequestDetail | null>(null);
  if (created) {
    return <Confirmation request={created} />;
  }
  return (
    <div className="container mx-auto flex w-full max-w-3xl flex-col gap-6">
      <PageHeader
        title="New title request"
        subtitle="Start with the property address. We will check whether Hera supports it, but you can submit the address even when it cannot be identified."
        hideBackButton
      />
      <TitleRequestForm
        getToken={() => getAccessTokenSilently()}
        onCreated={setCreated}
      />
    </div>
  );
}

export const Route = createFileRoute("/title/new")({
  component: function NewTitleRequestPage() {
    if (!titleAuthConfigured()) {
      return <p>Log in is not configured for title requests.</p>;
    }
    return (
      <AuthenticatedGuard
        allowedUserTypes={[UserType.TITLE_REQUESTOR, UserType.OPERATOR]}
        loginConnection={getTitleLoginConnection()}
      >
        <TitleRequestHome />
      </AuthenticatedGuard>
    );
  },
});
